import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { get } from 'lodash'
import { useDeploymentEvents } from '../shared/websocket'
import { useServices } from './ServicesContext'
import useProjectsResources from '../shared/resources/api/v1/admin/docker/useProjectsResources'
import useDeploymentsResources from '../shared/resources/api/v1/admin/docker/useDeploymentsResources'
import useIngressControllersResources from '../shared/resources/api/v1/admin/docker/useIngressControllersResources'
import useThrottledCallback from '../shared/hooks/useThrottledCallback'

const ProjectContext = createContext(null)

export function ProjectProvider({ children }) {
  const { teamSlug, projectSlug } = useParams()
  const { servicesByProjectId, loading: servicesLoading } = useServices()
  const { get: getProject } = useProjectsResources(teamSlug)
  const { all: getAllDeployments } = useDeploymentsResources(teamSlug, projectSlug)
  const { all: getAllIngressControllers } = useIngressControllersResources(teamSlug, projectSlug)
  const [project, setProject] = useState(null)
  const [deployments, setDeployments] = useState([])
  const [ingressControllers, setIngressControllers] = useState([])
  const [loading, setLoading] = useState(true)
  const [deploymentsLoading, setDeploymentsLoading] = useState(true)
  const [ingressControllersLoading, setIngressControllersLoading] = useState(true)
  const [error, setError] = useState(null)
  const loadedIdRef = useRef(null)
  const projectRef = useRef(project)
  projectRef.current = project

  // Get services for this project from the global ServicesContext
  const services = useMemo(() => {
    if (!project) return []
    return get(servicesByProjectId, project.id, [])
  }, [servicesByProjectId, project])

  const loadProject = useCallback(() => {
    if (!projectSlug) return Promise.resolve(null)
    if (loadedIdRef.current === projectSlug && projectRef.current) {
      return Promise.resolve(projectRef.current)
    }

    setLoading(true)
    setError(null)
    return getProject(projectSlug)
      .then((response) => {
        setProject(response.project)
        loadedIdRef.current = projectSlug
        return response.project
      })
      .catch((err) => {
        setError(err)
        throw err
      })
      .finally(() => {
        setLoading(false)
      })
  }, [projectSlug, getProject])

  const loadDeployments = useCallback(() => {
    if (!projectSlug) return Promise.resolve([])

    setDeploymentsLoading(true)
    return getAllDeployments()
      .then((response) => {
        setDeployments(response.deployments)
        return response.deployments
      })
      .catch((err) => {
        setDeployments([])
        return []
      })
      .finally(() => {
        setDeploymentsLoading(false)
      })
  }, [projectSlug, getAllDeployments])

  const loadIngressControllers = useCallback(() => {
    if (!projectSlug) return Promise.resolve([])

    setIngressControllersLoading(true)
    return getAllIngressControllers()
      .then((response) => {
        setIngressControllers(response.ingressControllers)
        return response.ingressControllers
      })
      .catch((err) => {
        setIngressControllers([])
        return []
      })
      .finally(() => {
        setIngressControllersLoading(false)
      })
  }, [projectSlug, getAllIngressControllers])

  useEffect(() => {
    loadProject()
    loadDeployments()
    loadIngressControllers()
  }, [projectSlug])

  const throttledLoadDeployments = useThrottledCallback(loadDeployments)

  useDeploymentEvents({
    onCreated: throttledLoadDeployments,
    onUpdated: throttledLoadDeployments,
  })

  const value = useMemo(() => ({
    project, services, deployments, ingressControllers,
    loading, servicesLoading, deploymentsLoading, ingressControllersLoading,
    error, reload: loadProject, reloadDeployments: loadDeployments, reloadIngressControllers: loadIngressControllers,
  }), [project, services, deployments, ingressControllers, loading, servicesLoading, deploymentsLoading, ingressControllersLoading, error, loadProject, loadDeployments, loadIngressControllers])

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  )
}

export function useProject() {
  const context = useContext(ProjectContext)
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider')
  }
  return context
}
